import PropTypes from 'prop-types'
import Papa from 'papaparse'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'

import DialogBody from 'components/Dialog/DialogBody'
import DialogTitle from 'components/Dialog/DialogTitle'
import DialogActions from 'components/Dialog/DialogActions'

import { createAttendantsWithRolesInBulk } from 'store/Attendants/actions'
import { getOrgName } from 'store/AppState/selector'
import fileValidation from 'components/PosAccounts/utils/validation'
import {
  orgAttendantsFileErrorMessagesCodes,
  orgAttendantsFileErrorMessages,
} from 'components/PosAccounts/utils'
import uploadFileUtils from 'components/PosAccounts/utils/uploadFile'
import { UPLOAD_ORG_ATTENDANTS_STEPS } from 'resources'
import { ReactComponent as CloseIcon } from 'assets/icons/closeIcon.svg'

const UploadedFile = ({
  setParsedFile,
  setAttendantsWithApiErrors,
  setStep,
  file,
  handleUploadDialogClose,
}) => {
  const dispatch = useDispatch()
  const orgName = useSelector(getOrgName)

  const handleUploadCSV = (uploadedAttendants) => {
    setParsedFile(uploadedAttendants)
    const attendantsWithErrors =
      fileValidation.orgAttendatsRowsWithErrors(uploadedAttendants)
    const rowsWithoutErrors =
      fileValidation.orgAttendatsRowsWithoutErrors(uploadedAttendants)

    // if every row has errors no need to proceed and make request to API
    if (!rowsWithoutErrors.length) {
      setStep(UPLOAD_ORG_ATTENDANTS_STEPS.ERRORS_PREVIEW)
      return
    }

    const onCreateAttendantsInBulk = (createAttendantWithRoleData) =>
      dispatch(
        createAttendantsWithRolesInBulk(createAttendantWithRoleData)
      ).then((errors) => {
        // All uploaded attendants are valid and there are no errors from the API after creation
        if (!errors && !attendantsWithErrors.length) {
          toast.success('POS Accounts created successfully!')
          handleUploadDialogClose()
          return
        }

        // There are errors from the API after creation
        const isAtLeastOneAttendantCreated = errors?.attendants?.some(
          (attendant) => !attendant?.errors
        )

        if (isAtLeastOneAttendantCreated) {
          toast.success(
            'Success! The valid POS Accounts have been created'
          )
        }

        // take the attendants with errors and transform them to be displayed in the UI
        // beacasue correct object keys are necessary to display errors in the UI
        const attendantsWithAPIErrors =
          uploadFileUtils.transformAttendantsWithAPIErrors(errors)

        setAttendantsWithApiErrors(attendantsWithAPIErrors)

        // if there are attendants with errors from the API or attendants with errors from the file
        // show the errors preview step
        if (
          attendantsWithAPIErrors?.length ||
          attendantsWithErrors.length
        ) {
          setStep(UPLOAD_ORG_ATTENDANTS_STEPS.ERRORS_PREVIEW)
          return
        }
      })

    uploadFileUtils.handleAttendantFormInBulkSubmit(
      rowsWithoutErrors,
      orgName,
      onCreateAttendantsInBulk
    )
  }

  const parseCsvFile = (file) => {
    Papa.parse(file, {
      skipEmptyLines: 'greedy',
      header: true,
      complete: (result) => {
        const areHeadersValid = fileValidation.validateFileHeaders(
          result.meta.fields
        )

        // validate file structure - headers, columns, delimiter
        if (result.errors.length) {
          setStep(UPLOAD_ORG_ATTENDANTS_STEPS.UPLOAD_FILE)
          toast.error(
            orgAttendantsFileErrorMessages.get(
              orgAttendantsFileErrorMessagesCodes.INVALID_FILE
            )
          )
          return
        }

        if (!areHeadersValid) {
          setStep(UPLOAD_ORG_ATTENDANTS_STEPS.UPLOAD_FILE)
          toast.error(
            orgAttendantsFileErrorMessages.get(
              orgAttendantsFileErrorMessagesCodes.INVALID_HEADERS
            )
          )
          return
        }

        // remove errors column from the parsed file
        const parsedFileWithoutErrorsColumn =
          fileValidation.removeErrorsColumn(result.data)

        handleUploadCSV(parsedFileWithoutErrorsColumn)
      },
    })
  }

  return (
    <>
      <DialogTitle>
        <header className="upload-file__header">
          <h3 className="upload-file__title">
            Upload Organization Attendants
          </h3>
          <button
            type="button"
            className="upload-file__close-dialog-icon"
            onClick={handleUploadDialogClose}
          >
            <CloseIcon />
          </button>
        </header>
      </DialogTitle>
      <DialogBody classNames="font-size-middle">
        <div className="font-weight-bold">File to upload :</div>
        <div>{file?.name}</div>
      </DialogBody>
      <DialogActions>
        <div className="upload-file__actions">
          <button
            type="button"
            onClick={() =>
              setStep(UPLOAD_ORG_ATTENDANTS_STEPS.UPLOAD_FILE)
            }
            className="upload-file__button"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => parseCsvFile(file)}
            className="upload-file__button"
          >
            Next
          </button>
        </div>
      </DialogActions>
    </>
  )
}

UploadedFile.propTypes = {
  setStep: PropTypes.func,
  setAttendantsWithApiErrors: PropTypes.func,
  setParsedFile: PropTypes.func,
  file: PropTypes.object,
  handleUploadDialogClose: PropTypes.func,
}

export default UploadedFile
